/* FILE: frontend/src/features/canada/pages/RfqForm.jsx */
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation, Trans } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

// ⚡ ADDED: Construction Chapters for descriptive search filtering
const CONSTRUCTION_CHAPTERS = ['25', '27', '32', '39', '44', '68', '69', '70', '72', '73', '74', '76', '83'];

// ⚡ ADDED: Synonym Dictionary for instant descriptive matching
const TRADE_SYNONYMS = [
    { keywords: ['beam', 'h-beam', 'i-beam', 'structural steel', 'girder'], code: '7216.32', description: 'Structural Steel H/I/U Beams & Sections' },
    { keywords: ['pipe', 'tube', 'tubing', 'hollow'], code: '7304.00', description: 'Steel Pipes, Tubes and Hollow Profiles' },
    { keywords: ['lumber', 'wood', 'timber', 'board', 'plywood'], code: '4407.11', description: 'Sawn Wood, Lumber & Timber (Coniferous)' },
    { keywords: ['wire', 'cable', 'electrical'], code: '8544.11', description: 'Insulated Copper Wire & Electrical Cables' },
    { keywords: ['cement', 'concrete', 'mortar'], code: '2523.29', description: 'Portland Cement & Hydraulic Cements' },
    { keywords: ['drywall', 'gypsum', 'plasterboard'], code: '6809.11', description: 'Plasterboards & Drywall Panels' }
];

export default function RfqForm() {
  const { t } = useTranslation(); 
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // 1. Intercept and safely decode URL parameters (with crash protection)
  const queryParams = new URLSearchParams(location.search);
  let rawUrlItem = queryParams.get('item') || '';
  try {
      rawUrlItem = decodeURIComponent(rawUrlItem);
  } catch (e) {
      // Silently catch malformed URI strings so the app doesn't crash
      console.warn("Malformed URL parameter ignored.");
  }

  const { 
      preselectedSupplier, 
      autoFillCode: stateCode, 
      specTemplate, 
      materialName 
  } = location.state || {};
  
  const isDirectRfq = !!preselectedSupplier;
  const rawPassedCode = rawUrlItem || stateCode || '';
  
  // 2. Safely parse the beautiful format if passed directly
  const extractedMatch = rawPassedCode.match(/\(([^)]+)\)/);
  const codeOnly = extractedMatch ? extractedMatch[1].trim() : rawPassedCode.trim();
  const nameOnly = extractedMatch ? rawPassedCode.split('(')[0].trim() : materialName || '';

  // 3. Initialize State
  const [formData, setFormData] = useState({
    hs_code: (nameOnly && codeOnly) ? `${nameOnly} (${codeOnly})` : rawPassedCode,
    material_name: nameOnly,
    postal_code: '',
    quantity: '',
    uom: 'Each (EA)', 
    technical_specs: specTemplate || '' 
  });

  // The Lock: Prevents ghost dropdowns from opening on page load
  const [isSelectionLocked, setIsSelectionLocked] = useState(!!nameOnly || !!codeOnly);

  // Autocomplete State & Refs
  const dropdownRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearchingDb, setIsSearchingDb] = useState(false);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [legalConsent, setLegalConsent] = useState(false);

  // ⚡ THE INITIALIZATION FIX: Protect 6-Digit Parent Categories
  useEffect(() => {
      // If we only received a raw code (e.g. 7216.32), fetch the beautiful description
      if (codeOnly && !nameOnly) {
          const resolveFromDb = async () => {
              const numericSearch = codeOnly.replace(/,/g, '.').replace(/[^0-9.]/g, '');
              if (numericSearch.length >= 2) {
                  
                  // FIX 1: Check Local Synonyms first. This protects 6-digit parent categories!
                  const localMatch = TRADE_SYNONYMS.find(s => s.code === numericSearch);
                  if (localMatch) {
                      setIsSelectionLocked(true); 
                      setFormData(prev => ({
                          ...prev,
                          hs_code: `${localMatch.description} (${localMatch.code})`,
                          material_name: localMatch.description
                      }));
                      return;
                  }

                  // FIX 2: Check DB for an EXACT match first
                  const { data: exactData } = await supabase
                      .from('hs_codes')
                      .select('code, description')
                      .eq('code', numericSearch)
                      .maybeSingle();

                  if (exactData) {
                      setIsSelectionLocked(true); 
                      setFormData(prev => ({
                          ...prev,
                          hs_code: `${exactData.description} (${exactData.code})`,
                          material_name: exactData.description
                      }));
                      return;
                  }

                  // FIX 3: Only fallback to .ilike (which might add .00) if no exact match exists
                  const { data: ilikeData } = await supabase
                      .from('hs_codes')
                      .select('code, description')
                      .ilike('code', `${numericSearch}%`)
                      .order('code')
                      .limit(1)
                      .maybeSingle();
                      
                  if (ilikeData) {
                      setIsSelectionLocked(true); 
                      setFormData(prev => ({
                          ...prev,
                          hs_code: `${ilikeData.description} (${ilikeData.code})`,
                          material_name: ilikeData.description
                      }));
                  }
              }
          };
          resolveFromDb();
      }
  }, [codeOnly, nameOnly]);

  // Fetch User Profile to default the Delivery Postal Code
  useEffect(() => {
    const fetchBuyerData = async () => {
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('postal_code')
          .eq('id', user.id)
          .single();
          
        if (profile?.postal_code) {
          setFormData(prev => ({ ...prev, postal_code: profile.postal_code }));
        }
      }
    };
    fetchBuyerData();
  }, [user]);

  // ⚡ BUG FIX: Fixed the ReferenceError crash by properly matching the rawUrlItem variable name
  useEffect(() => {
      if (rawUrlItem && rawUrlItem !== rawPassedCode) {
          setFormData(prev => ({ ...prev, hs_code: rawUrlItem, material_name: '' }));
      }
  }, [rawUrlItem, rawPassedCode]);

  // ⚡ Live Supabase Autocomplete (Synced with Directory Search Logic)
  useEffect(() => {
      const searchTerm = formData.hs_code;
      
      // Respect the lock and kill the ghost dropdown
      if (isSelectionLocked) {
          setShowDropdown(false);
          return;
      }

      if (!searchTerm || searchTerm.length < 2) {
          setSuggestions([]);
          setShowDropdown(false);
          return;
      }

      if (searchTerm.includes('(') && searchTerm.includes(')')) {
          setShowDropdown(false);
          setSuggestions([]);
          return;
      }

      const fetchSuggestions = async () => {
          setIsSearchingDb(true);
          try {
              const queryLower = searchTerm.toLowerCase();
              let allMatches = [];

              // 1. Check Hardcoded Synonyms (Fast fallback)
              TRADE_SYNONYMS.forEach(item => {
                  if (item.keywords.some(kw => queryLower.includes(kw))) {
                      allMatches.push({ code: item.code, description: item.description });
                  }
              });

              // 2. Query live 'hs_synonyms' Supabase table
              const { data: dbSynonyms } = await supabase
                  .from('hs_synonyms')
                  .select('colloquial_term, target_hs_code')
                  .ilike('colloquial_term', `%${queryLower}%`)
                  .limit(5);

              if (dbSynonyms) {
                  dbSynonyms.forEach(s => {
                      allMatches.push({ code: s.target_hs_code, description: s.colloquial_term });
                  });
              }

              // 3. Query official HS Database
              let query = supabase.from('hs_codes').select('code, description');
              
              const numericSearch = searchTerm.replace(/,/g, '.').replace(/[^0-9.]/g, '');            
              
              if (numericSearch.length >= 2) {
                  // If it looks like a number, search codes
                  query = query.ilike('code', `${numericSearch}%`);
              } else {
                  // If it looks like words, search descriptions
                  const words = searchTerm.trim().split(/\s+/).filter(w => w.length > 2);
                  const chapterFilter = CONSTRUCTION_CHAPTERS.map(ch => `code.ilike.${ch}%`).join(',');
                  query = query.or(chapterFilter);
                  words.forEach(w => {
                      query = query.ilike('description', `%${w}%`);
                  });
              }

              const { data: officialCodes, error } = await query.limit(5);
              if (error) throw error;
              
              // 4. Merge all sources and remove duplicates
              const combined = [...allMatches, ...(officialCodes || [])];
              const unique = Array.from(new Map(combined.map(item => [item.code, item])).values());

              setSuggestions(unique.slice(0, 6));
              
              // Only force open if the user is actively focused on the input
              if (document.activeElement?.name === 'hs_code') {
                  setShowDropdown(unique.length > 0);
              }

          } catch (err) {
              console.error("Autocomplete Error:", err);
          } finally {
              setIsSearchingDb(false);
          }
      };

      const delayDebounceFn = setTimeout(() => { fetchSuggestions(); }, 300); 
      return () => clearTimeout(delayDebounceFn);
  }, [formData.hs_code, isSelectionLocked]);

  // Close dropdown when clicking outside
  useEffect(() => {
      const handleClickOutside = (event) => {
          if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
              setShowDropdown(false);
          }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Unlock autocomplete if the user starts typing again
    if (name === 'hs_code') {
        setIsSelectionLocked(false);
        setFormData(prev => ({ ...prev, material_name: '' }));
    }
  };

  const handleSuggestionClick = (item) => {
      // Set visual string, wipe suggestions, and lock dropdown
      setIsSelectionLocked(true);
      setFormData(prev => ({
          ...prev,
          hs_code: `${item.description} (${item.code})`,
          material_name: item.description
      }));
      setSuggestions([]);
      setShowDropdown(false);
  };

  const handlePostalCodeChange = (e) => {
    let val = e.target.value.toUpperCase();
    val = val.replace(/[^A-Z0-9]/g, '');
    if (val.length > 6) val = val.substring(0, 6);
    if (val.length > 3) val = val.substring(0, 3) + ' ' + val.substring(3);
    setFormData(prev => ({ ...prev, postal_code: val }));
  };

  const validatePostalCode = (code) => {
    const regex = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;
    return regex.test(code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
        setError(t('rfq_form.signin_req'));
        return;
    }

    if (formData.postal_code && !validatePostalCode(formData.postal_code)) {
        setError("Please enter a valid 6-character Canadian Postal Code (e.g., A1A 1A1).");
        return;
    }
    
    if (!legalConsent) {
        setError("You must agree to the technical specifications liability clause to proceed.");
        return;
    }

    setLoading(true);
    setError('');

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

    try {
      const firstLetter = formData.postal_code.charAt(0);
      const provMap = { 'A':'NL', 'B':'NS', 'C':'PE', 'E':'NB', 'G':'QC', 'H':'QC', 'J':'QC', 'K':'ON', 'L':'ON', 'M':'ON', 'N':'ON', 'P':'ON', 'R':'MB', 'S':'SK', 'T':'AB', 'V':'BC', 'X':'NT', 'Y':'YT' };
      const derivedProvince = provMap[firstLetter] || 'ON';

      // Extract just the raw code from the formatted visual string
      const finalHsCode = formData.hs_code.match(/\(([^)]+)\)/) ? formData.hs_code.match(/\(([^)]+)\)/)[1].trim() : formData.hs_code.trim();

      const payload = {
          buyer_id: user.id,
          supplier_id: isDirectRfq ? preselectedSupplier.id : null,
          rfq_type: isDirectRfq ? 'direct' : 'concierge',
          hs_code: finalHsCode, // Safe submission
          destination_province: derivedProvince, 
          destination_postal_code: formData.postal_code, 
          quantity: Number(formData.quantity),
          uom: formData.uom,
          technical_specs: formData.technical_specs,
          status: 'sourcing_active'
      };

      const response = await fetch(`${apiUrl}/rfqs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
          throw new Error(result.error || `Server responded with status ${response.status}`);
      }
      
      setSuccess(true);
    } catch (err) {
      setError(`Error: ${err.message || "Failed to submit RFQ."}`);
    } finally {
      setLoading(false); 
    }
  };

  if (!user) {
      return (
        <div className="min-h-screen bg-gray-50 py-16 px-4 font-sans">
            <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-center">
                <span className="text-4xl mb-4 block">🔒</span>
                <h2 className="text-xl font-bold text-gray-900 mb-2">{t('rfq_form.signin_title')}</h2>
                <p className="text-gray-600 text-sm mb-6">
                    {t('rfq_form.signin_desc')}
                </p>
                <button 
                    onClick={() => navigate('/login', { state: { returnTo: '/rfq/new', autoFillCode, specTemplate } })}
                    className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    {t('rfq_form.signin_btn')}
                </button>
            </div>
        </div>
      );
  }

  if (success) {
      return (
          <div className="min-h-screen bg-gray-50 py-16 px-4 font-sans">
              <div className="max-w-2xl mx-auto bg-white p-10 rounded-2xl shadow-lg border border-gray-100 text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <span className="text-4xl text-green-500">✓</span>
                  </div>
                  
                  <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
                      {isDirectRfq ? t('rfq_form.success_direct') : t('rfq_form.success_concierge')}
                  </h2>
                  <p className="text-lg text-gray-600 mb-8 max-w-lg mx-auto">
                    {isDirectRfq ? (
                      <Trans i18nKey="rfq_form.success_direct_desc" values={{ code: formData.hs_code, company: preselectedSupplier?.company_name }}>
                        Your RFQ for <strong>{formData.hs_code}</strong> has been sent directly to <strong>{preselectedSupplier?.company_name}</strong>. You will be notified when they submit a quote.
                      </Trans>
                    ) : (
                      <Trans i18nKey="rfq_form.success_concierge_desc" values={{ code: formData.hs_code }}>
                        Our Concierge team is reviewing your requirements for <strong>{formData.hs_code}</strong>. We will manually source competitive bids from verified Canadian Suppliers soon.
                      </Trans>
                    )}
                  </p>
                  
                  <div className="flex justify-center gap-4">
                      <button onClick={() => navigate('/directory')} className="px-6 py-3 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition-colors">
                          {t('rfq_form.back_dir_btn')}
                      </button>
                      <button onClick={() => navigate('/inbox')} className="px-6 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-md">
                          {t('rfq_form.go_inbox')}
                      </button>
                  </div>
              </div>
          </div>
      );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-3xl mx-auto">
        
        <div className="mb-8">
          <Link to="/directory" className="text-sm font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-4">
              {t('rfq_form.back_dir')}
          </Link>
          
          {isDirectRfq ? (
            <>
              <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-3">
                 <span className="text-3xl">📄</span> {t('rfq_form.direct_rfq')}
              </h1>
              <p className="mt-2 text-gray-600">
                 {t('rfq_form.req_direct')} <strong className="text-gray-900">{preselectedSupplier.company_name}</strong>.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-3">
                 <span className="text-3xl">🏗️</span> {t('rfq_form.concierge_rfq')}
              </h1>
              <p className="mt-2 text-gray-600">
                 {t('rfq_form.tell_us')}
              </p>
            </>
          )}
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="p-8 space-y-6">
                
                {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 text-sm text-red-700 font-bold">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative flex flex-col w-full" ref={dropdownRef}>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('rfq_form.target_mat')}</label>
                        <input 
                            type="text" required name="hs_code"
                            value={formData.hs_code} 
                            onChange={handleChange}
                            onFocus={() => { if (suggestions.length > 0 && !isSelectionLocked) setShowDropdown(true); }}
                            placeholder={t('rfq_form.target_mat_ph', { defaultValue: 'e.g. 7411.10' })}
                            className="w-full rounded-lg border-gray-300 bg-white p-3 focus:ring-blue-500 focus:border-blue-500 transition-all font-mono shadow-sm border"
                            autoComplete="off"
                        />

                        {/* Live Dropdown Menu */}
                        {showDropdown && (
                          <div className="absolute top-[76px] z-50 w-full mt-1 bg-white border border-blue-200 rounded-lg shadow-xl overflow-hidden animate-fade-in max-h-64 overflow-y-auto">
                              {isSearchingDb ? (
                                   <div className="p-3 text-sm text-gray-500 flex items-center gap-2">
                                       <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                       Searching database...
                                   </div>
                              ) : (
                                  <ul>
                                      {suggestions.map((item, index) => (
                                          <li 
                                              key={index}
                                              onClick={() => handleSuggestionClick(item)}
                                              className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 flex items-start gap-3 transition-colors"
                                          >
                                              <span className="text-blue-600 mt-0.5">🔍</span>
                                              <div>
                                                  <p className="text-sm font-semibold text-gray-900 line-clamp-1">{item.description}</p>
                                                  <p className="text-xs font-mono text-blue-700 mt-0.5">HS {item.code}</p>
                                              </div>
                                          </li>
                                      ))}
                                  </ul>
                              )}
                          </div>
                        )}

                        {/* Descriptive Name Badge */}
                        <div className="min-h-[32px] mt-2">
                            {formData.material_name ? (
                              <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 px-2.5 py-1.5 rounded inline-block font-semibold">
                                {formData.material_name}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-400">
                                Search by number to lock in the exact material description.
                              </p>
                            )}
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery Postal Code</label>
                        <input 
                            type="text" required name="postal_code"
                            value={formData.postal_code} onChange={handlePostalCodeChange}
                            maxLength={7}
                            placeholder="e.g. A1A 1A1"
                            className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 focus:bg-white focus:border-blue-500 transition-all uppercase font-mono"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('rfq_form.qty')}</label>
                        <input 
                            type="number" required min="1" name="quantity"
                            value={formData.quantity} onChange={handleChange}
                            placeholder="e.g. 5000"
                            className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 focus:bg-white focus:border-blue-500 transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('rfq_form.uom')}</label>
                        <select 
                            required name="uom"
                            value={formData.uom} onChange={handleChange}
                            className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 focus:bg-white focus:border-blue-500 transition-all"
                        >
                            <optgroup label="Weight & Mass (Metals & Cement)">
                                <option value="Hundredweight (cwt)">Hundredweight (cwt)</option>
                                <option value="Pounds (lbs)">Pounds (lbs)</option>
                                <option value="Kilograms (kg)">Kilograms (kg)</option>
                                <option value="Metric Tonnes (MT)">Metric Tonnes (MT)</option>
                                <option value="Short Tons (ST)">Short Tons (ST)</option>
                            </optgroup>
                            <optgroup label="Length (Extrusions & Rebar)">
                                <option value="Linear Feet (LF)">Linear Feet (LF)</option>
                                <option value="Linear Meters (LM)">Linear Meters (LM)</option>
                            </optgroup>
                            <optgroup label="Area & Volume (Timber & Concrete)">
                                <option value="Square Feet (SF)">Square Feet (SF)</option>
                                <option value="Square Meters (SQM)">Square Meters (SQM)</option>
                                <option value="Cubic Yards (CY)">Cubic Yards (CY)</option>
                                <option value="Cubic Meters (CM)">Cubic Meters (CM)</option>
                                <option value="Board Feet (BF)">Board Feet (BF)</option>
                            </optgroup>
                            <optgroup label="Count & Packaged Goods">
                                <option value="Each (EA)">Each (EA)</option>
                                <option value="Pieces (PCS)">Pieces (PCS)</option>
                                <option value="Skids / Pallets">Skids / Pallets</option>
                                <option value="Bundles">Bundles</option>
                                <option value="Truckload (TL)">Truckload (TL)</option>
                            </optgroup>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('rfq_form.specs')}</label>
                    <textarea 
                        required rows="5" name="technical_specs"
                        value={formData.technical_specs} onChange={handleChange}
                        placeholder={t('rfq_form.specs_ph')}
                        className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 focus:bg-white focus:border-blue-500 transition-all"
                    />
                </div>

                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input 
                            type="checkbox" 
                            checked={legalConsent} 
                            onChange={(e) => setLegalConsent(e.target.checked)}
                            className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 bg-white"
                            required
                        />
                        <span className="text-sm text-gray-800 font-medium leading-snug">
                            I confirm these technical specifications meet the engineering requirements of my project. TariffBeater Inc. is a venue-only platform and is not liable for specification errors, structural failures, or procurement delays.
                        </span>
                    </label>
                </div>

            </div>

            <div className="bg-gray-50 px-8 py-5 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-500 hidden md:block">
                    {t('rfq_form.confidential')}
                </p>
                
                <button 
                    type="submit" 
                    disabled={loading || !legalConsent}
                    className="w-full md:w-auto px-8 py-3 bg-gray-900 text-white font-bold rounded-lg shadow-md hover:bg-gray-800 transition-all disabled:opacity-50"
                >
                    {loading ? t('rfq_form.submitting') : (isDirectRfq ? t('rfq_form.send_direct') : t('rfq_form.submit_concierge'))}
                </button>
            </div>
        </form>

      </div>
    </div>
  );
}